import { ApiService, CustomHttpParams } from '@abb-procure/api';
import { EntityIdAliasPrefixes } from '@abb-procure/constants';
import { Injectable } from '@angular/core';
import * as t from 'io-ts';
import { AddSearchActivityRequest } from '../types/add-search-activity-request.type';
import { SearchDefaultResponse } from '../types/search-default-response.type';
import { SearchOptionsResponse } from '../types/search-options-response.type';
import { SearchRequest } from '../types/search-request.type';
import { SearchResponse } from '../types/search-response.type';

export type SearchOptions = {
  highlightPreTag: string;
  highlightPostTag: string;
  searchTerm?: string;
  type?: EntityIdAliasPrefixes;
  top?: number;
  skip?: number;
  showInactive?: boolean;
  showClosed?: boolean;
  includeTotalCount?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class SearchApiService extends ApiService {
  async getSearchOptions(): Promise<SearchOptionsResponse | null> {
    try {
      return await this.get<SearchOptionsResponse>(
        'Search/options',
        SearchOptionsResponse,
      );
    } catch (error) {
      return this.catchError(
        error,
        'There was an issue while retrieving the search options.',
      );
    }
  }

  async getSearchDefaults(
    pageIndex: number,
    pageSize: number,
  ): Promise<SearchDefaultResponse | null> {
    try {
      const queryParams = new CustomHttpParams({
        pageIndex,
        pageSize,
        orderBy: 'name',
        orderIsAscending: true,
      });

      return await this.get<SearchDefaultResponse>(
        'Search/default',
        SearchDefaultResponse,
        queryParams,
      );
    } catch (error) {
      return this.catchError(
        error,
        'There was an issue while retrieving the search defaults.',
      );
    }
  }

  async search(request: SearchRequest): Promise<SearchResponse | null> {
    const errorMessage =
      'There was an issue while retrieving the search results.';

    try {
      const response = await this.put<SearchResponse, SearchRequest>(
        'Search',
        SearchResponse,
        request,
      );

      if (!response) {
        return response;
      }

      if (response.hasError) {
        throw new Error(response.errorMessage ?? errorMessage);
      }

      return response;
    } catch (error) {
      return this.catchError(error, errorMessage);
    }
  }

  async addSearchActivity(
    request: AddSearchActivityRequest,
  ): Promise<boolean | null> {
    try {
      return await this.post<boolean, AddSearchActivityRequest>(
        'Search',
        t.boolean,
        request,
      );
    } catch (error) {
      return this.catchError(
        error,
        'There was an issue while adding the search activity.',
      );
    }
  }

  async getSuggestions(request: SearchRequest): Promise<string[] | null> {
    try {
      return await this.put<string[], SearchRequest>(
        'Search/suggestion',
        t.array(t.string),
        request,
      );
    } catch (error) {
      return this.catchError(
        error,
        'There was an issue while retrieving suggestions.',
      );
    }
  }

  async getAutocomplete(request: SearchRequest): Promise<string[] | null> {
    try {
      return await this.put<string[], SearchRequest>(
        'Search/autocomplete',
        t.array(t.string),
        request,
      );
    } catch (error) {
      return this.catchError(
        error,
        'There was an issue while retrieving autocomplete.',
      );
    }
  }
}
